import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaCalendarAlt, FaUser, FaTag, FaFacebookF, FaLinkedinIn, FaInstagram, FaLink, FaCheck } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../contexts/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';

const categoryColors = {
  'Prestasi': 'bg-yellow-100 text-yellow-800',
  'Event': 'bg-blue-100 text-blue-800',
  'Kolaborasi': 'bg-green-100 text-green-800',
  'Program': 'bg-purple-100 text-purple-800',
  'Umum': 'bg-gray-100 text-gray-700',
};

const NewsDetail = () => {
  const { slug } = useParams();
  const { t } = useLanguage();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [copied, setCopied] = useState(false);
  usePageTitle(article ? article.title : null);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('news')
        .select('*')
        .eq('slug', slug)
        .eq('is_published', true)
        .single();

      if (error || !data) {
        setArticle(null);
      } else {
        setArticle(data);
        // Fetch related articles (same category, exclude current)
        const { data: related } = await supabase
          .from('news')
          .select('id, title, slug, excerpt, cover_url, category, created_at')
          .eq('is_published', true)
          .eq('category', data.category)
          .neq('id', data.id)
          .order('created_at', { ascending: false })
          .limit(3);
        setRelatedArticles(related || []);
      }
      setLoading(false);
    };
    fetchArticle();
  }, [slug]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <div className="text-6xl mb-4">📰</div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('newsDetail.notFoundTitle')}</h2>
        <p className="text-gray-500 mb-6">{t('newsDetail.notFoundDesc')}</p>
        <Link to="/berita" className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-primary-700 transition">
          <FaArrowLeft /> {t('newsDetail.backToNews')}
        </Link>
      </div>
    );
  }

  return (
    <div className="font-inter bg-gray-50 min-h-screen">
      {/* Cover Image */}
      {article.cover_url && (
        <div className="w-full h-64 sm:h-80 md:h-96 relative">
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10"></div>
          <img src={article.cover_url} alt={article.title} className="w-full h-full object-cover" />
        </div>
      )}

      {/* Article Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/berita" className="text-primary-600 hover:text-primary-800 text-sm inline-flex items-center gap-1 mb-6 transition-colors">
          <FaArrowLeft /> {t('newsDetail.backToNews')}
        </Link>

        {/* Category */}
        <div className="mb-4">
          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${categoryColors[article.category] || categoryColors['Umum']}`}>
            <FaTag className="text-[10px]" /> {article.category}
          </span>
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight mb-4">
          {article.title}
        </h1>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-8 pb-8 border-b border-gray-200">
          <span className="flex items-center gap-1.5">
            <FaCalendarAlt className="text-primary-400" /> {formatDate(article.created_at)}
          </span>
          {article.author_name && (
            <span className="flex items-center gap-1.5">
              <FaUser className="text-primary-400" /> {article.author_name}
            </span>
          )}
        </div>

        {/* Article Body */}
        <div
          className="prose prose-lg max-w-none
            prose-headings:text-gray-900 prose-headings:font-bold
            prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
            prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
            prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
            prose-a:text-primary-600 prose-a:underline prose-a:decoration-primary-300 hover:prose-a:decoration-primary-600
            prose-img:rounded-xl prose-img:shadow-md prose-img:my-6
            prose-blockquote:border-l-4 prose-blockquote:border-primary-400 prose-blockquote:bg-primary-50 prose-blockquote:rounded-r-xl prose-blockquote:px-5 prose-blockquote:py-4 prose-blockquote:not-italic
            prose-ul:list-disc prose-ol:list-decimal
            prose-li:text-gray-700
            prose-code:bg-gray-100 prose-code:text-red-600 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        {/* Share Buttons */}
        {(() => {
          const articleUrl = window.location.href;
          const shareText = article.title;
          return (
            <div className="mt-10 pt-8 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('newsDetail.share')}</h3>
              <div className="flex items-center gap-3">
                <a
                  href={`https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(articleUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center hover:bg-gray-800 transition-colors"
                  title="Share on X"
                >
                  <FaXTwitter className="text-sm" />
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(articleUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:bg-[#166FE5] transition-colors"
                  title="Share on Facebook"
                >
                  <FaFacebookF className="text-sm" />
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(articleUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#0A66C2] text-white flex items-center justify-center hover:bg-[#0958A8] transition-colors"
                  title="Share on LinkedIn"
                >
                  <FaLinkedinIn className="text-sm" />
                </a>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(articleUrl);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    copied
                      ? 'bg-green-500 text-white'
                      : 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 text-white hover:from-purple-600 hover:via-pink-600 hover:to-orange-500'
                  }`}
                  title={copied ? t('newsDetail.copied') : t('newsDetail.copyLink')}
                >
                  {copied ? <FaCheck className="text-sm" /> : <FaInstagram className="text-sm" />}
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(articleUrl);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    copied
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }`}
                  title={copied ? t('newsDetail.copied') : t('newsDetail.copyLink')}
                >
                  {copied ? <FaCheck className="text-sm" /> : <FaLink className="text-sm" />}
                </button>
              </div>
              {copied && (
                <p className="text-xs text-green-600 mt-2">{t('newsDetail.copiedMsg')}</p>
              )}
            </div>
          );
        })()}
      </div>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <div className="bg-white border-t border-gray-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('newsDetail.relatedArticles')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedArticles.map(item => (
                <Link key={item.id} to={`/berita/${item.slug}`} className="group bg-gray-50 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300 border border-gray-100">
                  {item.cover_url ? (
                    <img src={item.cover_url} alt={item.title} className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <div className="w-full h-40 bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center text-4xl">📰</div>
                  )}
                  <div className="p-4">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${categoryColors[item.category] || categoryColors['Umum']}`}>
                      {item.category}
                    </span>
                    <h3 className="text-sm font-bold text-gray-900 mt-2 line-clamp-2 group-hover:text-primary-700 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">{formatDate(item.created_at)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsDetail;
